import React, { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
  signOut,
} from "firebase/auth";
import { auth } from "./firebase";
import rigbyHead from "./rigby-head.png";
import { consentKey, validatePassword, requestVerificationEmail, passwordRules as rulesFor, RESEND_COOLDOWN_SECONDS } from "./authUtils";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function friendlyAuthError(error) {
  switch (error?.code) {
    case "auth/email-already-in-use":
      return "That email already has an account. Try signing in, or use Continue with Google.";
    case "auth/invalid-email":
      return "That email address doesn't look right.";
    case "auth/weak-password":
      return "Please choose a stronger password.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Connection problem. Check your internet and try again.";
    case "auth/password-does-not-meet-requirements":
      return "Your password needs 12+ characters with upper and lower case, a number and a symbol.";
    case "auth/operation-not-allowed":
      return "Email sign-up isn't enabled yet. Please contact support.";
    default:
      return "We couldn't create your account. Please try again.";
  }
}

export default function SignupPage({
  authLoading,
  googleIcon,
  onGoogle,
  onBackToLogin,
  onShowTerms,
  onShowFerpa,
}) {
  const [form, setForm] = useState({ firstName: "", email: "", password: "", confirm: "" });
  const [consent, setConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [resendMsg, setResendMsg] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const touch = (field) => () => setTouched((t) => ({ ...t, [field]: true }));

  // Validation is derived from the current values, so errors appear and clear as the user types.
  const allErrors = {};
  if (!form.firstName.trim()) allErrors.firstName = "First name is required";
  if (!EMAIL_RE.test(form.email.trim())) allErrors.email = "Enter a valid email address";
  if (!form.password) allErrors.password = "Password is required";
  else if (validatePassword(form.password)) allErrors.password = validatePassword(form.password);
  if (form.confirm !== form.password) allErrors.confirm = "Passwords do not match";

  // Show an error once the field was left, or while typing in the password fields, or after submit
  const errors = {
    firstName: (submitted || touched.firstName) && allErrors.firstName,
    email: (submitted || touched.email) && allErrors.email,
    password: (submitted || form.password) && allErrors.password,
    confirm: (submitted || form.confirm) && allErrors.confirm,
  };

  const passwordRules = rulesFor(form.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSubmitted(true);
    if (!consent) {
      setFormError("Please accept the Terms and Privacy Policy to continue.");
      return;
    }
    if (Object.keys(allErrors).length > 0) return;

    const email = form.email.trim().toLowerCase();
    setSubmitting(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, form.password);
      await updateProfile(cred.user, { displayName: form.firstName.trim() });
      if (!(await requestVerificationEmail(cred.user))) {
        await sendEmailVerification(cred.user); // fallback: Firebase's default email
      }
      // Record when consent was given; it is sent to the backend on first verified sign-in.
      try {
        localStorage.setItem(consentKey(email), new Date().toISOString());
      } catch (_) {
        // Storage unavailable: the consent screen will be shown at first sign-in instead.
      }
      await signOut(auth);
      setSentTo(email);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      console.error("Signup error:", error?.code, error?.message);
      setFormError(friendlyAuthError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || !sentTo) return;
    setResendMsg("");
    setSubmitting(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, sentTo, form.password);
      if (!cred.user.emailVerified && !(await requestVerificationEmail(cred.user))) {
        await sendEmailVerification(cred.user);
      }
      await signOut(auth);
      setResendMsg("Sent again. Check your inbox and spam folder.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      console.error("Resend verification error:", error?.code, error?.message);
      setResendMsg(
        error?.code === "auth/too-many-requests"
          ? "Too many requests. Please wait a few minutes."
          : "Couldn't resend right now. Try again shortly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || authLoading;

  return (
    <div className="rigby-login fade-in">
      <div className="rigby-login-brand">
        <div className="rigby-login-brand-bg" />
        <div className="rigby-login-brand-top">
          <img src={rigbyHead} alt="Rigby" className="rigby-login-mark" />
          <span className="rigby-login-wordmark">My Emotional Support Bot</span>
        </div>
        <div className="rigby-login-brand-mid">
          <div className="rigby-login-badge">
            <span className="rigby-login-badge-dot" />
            Join Rigby
          </div>
          <h1 className="rigby-login-headline">
            A calm place to land, <em>whenever you need it.</em>
          </h1>
          <p className="rigby-login-lede">
            Create an account in under a minute. We only ask for what we need: your first name,
            your email and a password.
          </p>
        </div>
      </div>

      <div className="rigby-login-form-panel">
        <div className="rigby-login-form-card">
          {sentTo ? (
            <>
              <h2 className="rigby-login-form-title">
                Check your <em>email.</em>
              </h2>
              <p className="rigby-login-form-subtitle">
                We sent a verification link to <strong>{sentTo}</strong>. Open it, then come back
                and sign in.
              </p>
              <p className="rigby-signup-hint">
                Can't find it? Look in your spam folder. The link works once.
              </p>
              <button
                type="button"
                className="rigby-login-submit-btn"
                onClick={onBackToLogin}
              >
                Go to sign in
              </button>
              <div className="rigby-login-guest-row">
                <button
                  type="button"
                  className="rigby-login-link rigby-signup-linkbtn"
                  onClick={handleResend}
                  disabled={cooldown > 0 || busy}
                >
                  {cooldown > 0 ? `Resend email in ${cooldown}s` : "Resend verification email"}
                </button>
              </div>
              {resendMsg && <p className="rigby-signup-hint" role="status">{resendMsg}</p>}
            </>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <h2 className="rigby-login-form-title">
                Create your <em>account.</em>
              </h2>
              <p className="rigby-login-form-subtitle">
                Already have one?{" "}
                <span className="rigby-login-link" onClick={onBackToLogin}>Sign in</span>
              </p>

              <button
                type="button"
                className="rigby-login-google-btn"
                onClick={onGoogle}
                disabled={busy}
              >
                {googleIcon} Sign up with Google
              </button>

              <div className="rigby-login-divider"><span>OR</span></div>

              <div className="rigby-login-field">
                <label htmlFor="su-first">First name</label>
                <input
                  id="su-first"
                  type="text"
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={set("firstName")}
                  onBlur={touch("firstName")}
                  aria-invalid={!!errors.firstName}
                />
                {errors.firstName && <div className="rigby-signup-error">{errors.firstName}</div>}
              </div>

              <div className="rigby-login-field">
                <label htmlFor="su-email">Email</label>
                <input
                  id="su-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@university.edu"
                  value={form.email}
                  onChange={set("email")}
                  onBlur={touch("email")}
                  aria-invalid={!!errors.email}
                />
                {errors.email && <div className="rigby-signup-error">{errors.email}</div>}
              </div>

              <div className="rigby-login-field">
                <label htmlFor="su-pass">Password</label>
                <div className="rigby-login-password-wrap">
                  <input
                    id="su-pass"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={set("password")}
                    aria-invalid={!!errors.password}
                  />
                  <button
                    type="button"
                    className="rigby-login-eye-btn"
                    onClick={() => setShowPassword((s) => !s)}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <ul className="rigby-signup-rules" aria-live="polite">
                  {passwordRules.map((r) => (
                    <li key={r.label} className={r.ok ? "ok" : ""}>
                      {r.ok ? "✓" : "•"} {r.label}
                    </li>
                  ))}
                </ul>
                {errors.password && !form.password && (
                  <div className="rigby-signup-error">{errors.password}</div>
                )}
              </div>

              <div className="rigby-login-field">
                <label htmlFor="su-confirm">Confirm password</label>
                <input
                  id="su-confirm"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={form.confirm}
                  onChange={set("confirm")}
                  aria-invalid={!!errors.confirm}
                />
                {errors.confirm && <div className="rigby-signup-error">{errors.confirm}</div>}
              </div>

              <label className="rigby-signup-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>
                  I agree to the{" "}
                  <button type="button" className="rigby-signup-linkbtn" onClick={onShowTerms}>
                    Terms
                  </button>{" "}
                  and{" "}
                  <button type="button" className="rigby-signup-linkbtn" onClick={onShowFerpa}>
                    Privacy Policy
                  </button>
                  . My conversations are never shared with my school.
                </span>
              </label>

              {formError && <div className="rigby-signup-error" role="alert">{formError}</div>}

              <button type="submit" className="rigby-login-submit-btn" disabled={busy}>
                {submitting ? "Creating account..." : "Create account"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
